import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { ViewerReturn } from "@constructionautomation/backend/convex/auth";
import {
  PLATFORM_ADMIN_ROLE,
  type PlatformRole,
} from "@constructionautomation/backend/types/platformRoles";

interface UserProfileContextValue {
  userProfile: ViewerReturn | null | undefined;
  platformRoles: PlatformRole[];
  isPlatformAdmin: boolean;
  isPending: boolean;
  error: Error | null;
}

const UserProfileContext = createContext<UserProfileContextValue | undefined>(
  undefined
);

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const { data: userProfile, isPending: isUserPending, error: userError } = useQuery({
    ...convexQuery(api.auth.viewer, {}),
    staleTime: 5 * 60 * 1000, // 5 minutes - user profile rarely changes
  });
  const { data: platformRolesData, isPending: isRolesPending, error: rolesError } = useQuery({
    ...convexQuery(api.admin.roles.getCurrentPlatformRoles, {}),
    staleTime: 5 * 60 * 1000,
  });

  const platformRoles = useMemo(
    () => [...((platformRolesData ?? []) as PlatformRole[])].sort((left, right) => left.localeCompare(right)),
    [platformRolesData]
  );
  const isPending = isUserPending || isRolesPending;
  const error =
    userError instanceof Error
      ? userError
      : rolesError instanceof Error
        ? rolesError
        : null;

  return (
    <UserProfileContext.Provider
      value={{
        userProfile,
        platformRoles,
        isPlatformAdmin: platformRoles.includes(PLATFORM_ADMIN_ROLE),
        isPending,
        error,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile(): UserProfileContextValue {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error(
      "useUserProfile must be used within a UserProfileProvider"
    );
  }
  return context;
}
